/* eslint-disable no-console */
/* global axios */
import NotificationSubscriptions from '../api/notificationSubscription';
import { useAlert } from 'dashboard/composables';

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!('serviceWorker' in navigator)) {
    // Service Worker isn't supported on this browser, disable or hide UI.
    return;
  }

  if (!('PushManager' in window)) {
    // Push isn't supported on this browser, disable or hide UI.
    return;
  }

  navigator.serviceWorker
    .register('/sw.js')
    .then(registration => callback(registration))
    .catch(registrationError => {
      // eslint-disable-next-line
      console.log('SW registration failed: ', registrationError);
    });
};

export const hasPushPermissions = () => {
  if ('Notification' in window) {
    return Notification.permission === 'granted';
  }
  return false;
};

const generateKeys = str =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const getPushSubscriptionPayload = subscription => ({
  subscription_type: 'browser_push',
  subscription_attributes: {
    endpoint: subscription.endpoint,
    p256dh: generateKeys(subscription.getKey('p256dh')),
    auth: generateKeys(subscription.getKey('auth')),
  },
});

// Smarticks: persist the subscription server-side. The request authenticates via
// the normal dashboard session/axios headers, so we do NOT gate on
// auth.hasAuthCookie() — the cw_d_session_info cookie is absent on some installs,
// which silently blocked registration and prevented agents from self-enabling push.
export const sendRegistrationToServer = subscription =>
  NotificationSubscriptions.create(getPushSubscriptionPayload(subscription));

// Smarticks: delete THIS browser's subscription server-side (full CRUD on disable).
// Browser rows are keyed by endpoint; the custom destroy override removes by
// endpoint, scoped to the current user. ApiClient.delete appends /:id, so call
// axios directly with the endpoint in the request body.
export const deleteSubscriptionFromServer = endpoint =>
  axios.delete('/api/v1/notification_subscriptions', { data: { endpoint } });

export const registerSubscription = (onSuccess = () => {}) => {
  if (!window.chatwootConfig.vapidPublicKey) {
    return;
  }
  navigator.serviceWorker.ready
    .then(serviceWorkerRegistration =>
      serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: window.chatwootConfig.vapidPublicKey,
      })
    )
    .then(sendRegistrationToServer)
    .then(() => {
      onSuccess();
    })
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error('Push subscription registration failed:', error);
      useAlert('This browser does not support desktop notification');
    });
};

export const requestPushPermissions = ({ onSuccess }) => {
  if (!('Notification' in window)) {
    // eslint-disable-next-line no-console
    console.warn('Notification is not supported');
    useAlert('This browser does not support desktop notification');
  } else if (Notification.permission === 'granted') {
    registerSubscription(onSuccess);
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission(permission => {
      if (permission === 'granted') {
        registerSubscription(onSuccess);
      }
    });
  } else {
    // Smarticks: previously silent — tell the agent push is blocked at the browser.
    useAlert(
      'Notifications are blocked for this site. Enable them in your browser settings, then try again.'
    );
  }
};
